import type React from 'react';
import {
  Accordion,
  Anchor,
  Box,
  DuoColorIcon,
  Heading,
  Icon,
  Paragraph,
  Text,
  ZillowLogo,
} from '@zillow/constellation-10';
import { IconDollarSignCircleFilled, IconTrendingFilled } from '@zillow/constellation-icons';
import EsusuIcon from '../../assets/EsusuIcon.svg';
import EsusuLogo from '../../assets/EsusuLogo.png';
import Image from 'next/image';
import { BenefitSection } from './elements/BenefitSection';
import { ClickstreamAuthTriggerSource, ClickstreamTriggerObject, emitFaqClickEvent } from '../../analytics/events';
import RequestZillowPaymentsModal from './elements/RequestZillowPaymentsModal';

export default function OverviewScreen() {
  return (
    <Box css={{ maxWidth: '768px' }}>
      <Box
        css={{
          display: 'flex',
          gap: 'tight',
          alignItems: 'center',
          marginBottom: 'loose',
          marginTop: 'loose',
        }}
      >
        <Box css={{ height: '600' }}>
          <ZillowLogo role="img" aria-label="Zillow" />
        </Box>
        <Box role="presentation" css={{ width: '1px', height: '600', backgroundColor: 'text.subtle' }} />
        <Box css={{ height: '600' }}>
          <Image src={EsusuLogo} alt="Esusu" width={100} height={24} />
        </Box>
      </Box>

      <Heading level={2} textStyle="heading-md" css={{ marginBottom: 'loose' }}>
        Start building your credit
      </Heading>

      <Paragraph css={{ textStyle: 'body-lg', marginBottom: 'loose' }}>
        Here’s how a credit building membership can help:
      </Paragraph>

      <Box css={{ marginBottom: 'loose' }}>
        <BenefitSection
          icon={
            <DuoColorIcon tone="success" onBackground="impact">
              <Image src={EsusuIcon} alt="" width={24} height={24} />
            </DuoColorIcon>
          }
          title="Sign up for $20 per year"
        >
          Join over <strong>5 million renters</strong> who report their rent payments with our trusted partner, Esusu.
        </BenefitSection>

        <BenefitSection
          icon={
            <DuoColorIcon tone="success" onBackground="impact">
              <Icon>
                <IconDollarSignCircleFilled />
              </Icon>
            </DuoColorIcon>
          }
          title="Esusu reports your on-time rent payments"
        >
          There’s no hard pulls on your credit or hidden fees, plus late or missed rent is <strong>never</strong>{' '}
          reported. However, consistent on-time payments help build your credit.
        </BenefitSection>

        <BenefitSection
          icon={
            <DuoColorIcon tone="success" onBackground="impact">
              <Icon>
                <IconTrendingFilled />
              </Icon>
            </DuoColorIcon>
          }
          title="Prepare for the future"
        >
          According to Esusu’s 2024 impact report, members have seen an average <strong>+53 points</strong> to their
          credit.* Rent reporting can help you start building a credit profile that supports your future home buying
          goals, without taking on debt.
        </BenefitSection>
      </Box>

      <Box css={{ marginBottom: 'looser' }}>
        <Accordion.Root>
          <Accordion.Item value="why-partner" onClick={() => emitFaqClickEvent('why_esusu')}>
            <Accordion.Header>
              <Paragraph>Why is Zillow partnering with Esusu?</Paragraph>
            </Accordion.Header>
            <Accordion.Panel>
              <Paragraph css={{ marginBottom: 'default' }}>
                Zillow is built on a deep understanding of movers’ needs and a motivation to help you accomplish your
                moving goals, whether that’s choosing your next rental or preparing to buy your first home. We believe
                rent reporting can help you start building a credit profile that supports your future home buying goals,
                without taking on debt.
              </Paragraph>
              <Paragraph css={{ marginBottom: 'default' }}>
                We’re partnering with{' '}
                <Anchor href="https://esusurent.com/" target="_blank" rel="noopener noreferrer">
                  Esusu
                </Anchor>
                , a financial technology company dedicated to expanding fair credit and financial access. Esusu helps
                renters build credit while giving property partners tools that promote financial stability.
              </Paragraph>
              <Paragraph>
                Through our partnership, you can report your rent for an <strong>exclusive annual rate of $20</strong>,{' '}
                helping you get credit for one of your current largest monthly expenses, your rent.
              </Paragraph>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="free-option">
            <Accordion.Header onClick={() => emitFaqClickEvent('zrm_version')}>
              <Paragraph>Is there a free option?</Paragraph>
            </Accordion.Header>
            <Accordion.Panel>
              <Paragraph css={{ marginBottom: 'default' }}>
                Yes, when your landlord collects rent with Zillow payments you’ll have the choice to opt-in to free rent
                reporting. Plus, there’s no fee to pay rent with Zillow when you pay with a direct bank transfer.
              </Paragraph>
              <Paragraph>
                Interested in this option?{' '}
                <RequestZillowPaymentsModal
                  ctaText="Ask your landlord to use Zillow payments today"
                  clickstreamTriggerObject={ClickstreamTriggerObject.FAQ_UPSELL}
                  authTriggerSource={ClickstreamAuthTriggerSource.ZRM_REQUEST_FAQ}
                />
              </Paragraph>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion.Root>
      </Box>

      <Text
        id="credit-disclaimer"
        css={{
          textStyle: 'body-xs',
          color: 'text.subtle',
          textAlign: 'center',
        }}
      >
        <sup>*</sup>Based on active renters at active properties from enrollment through Q4 2025. Average credit score
        increase calculated by comparing enrollment credit score to latest available credit score as of Q4 2025,
        treating renters with no credit score at enrollment as having a score of zero. Individual results vary and are
        not guaranteed.
      </Text>
    </Box>
  );
}
